import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import LayersIcon from "@mui/icons-material/Layers";
import BuildIcon from "@mui/icons-material/Build";

import { fetchGetAllPlanes, fetchDeletePlan } from "../../action";
import "../../css/styles.css";

const isMobile =
  !!navigator.userAgent.match(/iphone|android|blackberry/gi) || false;

const StyledTableHead = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#00a62b",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const ListadoPlanes = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [filtro, setFiltro] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const planes = useSelector((state) => state.app.itemPlanes || []);

  const cargarDatos = async () => {
    setIsLoading(true);
    await dispatch(fetchGetAllPlanes());
    setIsLoading(false);
  };

  useEffect(() => {
    cargarDatos();
  }, [dispatch]);

  const handleDeletePlan = (e, row) => {
    e.stopPropagation();

    Swal.fire({
      title: `Eliminar Plan`,
      text: `¿Confirma que desea eliminar el plan "${row.nombre}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed || result.value) {
        Swal.showLoading();
        try {
          await dispatch(fetchDeletePlan(row.id));
          await dispatch(fetchGetAllPlanes());
          Swal.fire({
            title: "Plan eliminado",
            text: "Se ha eliminado el plan exitosamente",
            icon: "success",
          });
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: "Ocurrió un error al eliminar el plan",
            icon: "error",
          });
        }
      }
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltro = (e) => {
    setFiltro(e.target.value);
    setPage(0);
  };

  const filteredItems = planes.filter((item) => {
    if (!filtro) return true;
    const search = filtro.toLowerCase();
    const nombre = item.nombre ? item.nombre.toLowerCase() : "";
    return nombre.includes(search);
  });

  const displayedItems =
    rowsPerPage > 0
      ? filteredItems.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
      : filteredItems;

  return (
    <div className="contenido">
      <div className="header">
        <h2>Administración de Planes</h2>
      </div>

      {/* Tabs / Sub-navigation */}
      <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
        <Grid item>
          <Button
            variant="contained"
            color="success"
            onClick={() => history.push("/planes")}
            sx={{ fontWeight: "bold" }}
          >
            Planes
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="success"
            onClick={() => history.push("/planes/servicios")}
          >
            Servicios
          </Button>
        </Grid>
      </Grid>

      <Grid container sx={{ mt: 1, mb: 2 }} justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={6} md={4} className="alignLeft">
          <Link to="/planes/agregar" style={{ textDecoration: "none" }}>
            <button className="boton">Crear plan</button>
          </Link>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className="alignLeft">
          <div style={{ position: "relative", width: "100%" }}>
            <input
              type="text"
              placeholder="Buscar plan..."
              className="buscar"
              onChange={handleFiltro}
              value={filtro}
            />
            <SearchIcon style={{ position: "absolute", right: 10, top: 10, color: "#888" }} />
          </div>
        </Grid>
      </Grid>

      <div className="main">
        {isLoading && (
          <Grid container justifyContent="center" sx={{ my: 4 }}>
            <CircularProgress color="success" />
          </Grid>
        )}

        {!isLoading && (
          <Paper sx={{ width: "100%", overflow: "hidden", marginBottom: "2rem" }}>
            <TableContainer>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <StyledTableHead align="left">Plan</StyledTableHead>
                    <StyledTableHead align="left">Venta o Arriendo</StyledTableHead>
                    <StyledTableHead align="left">Servicios</StyledTableHead>
                    <StyledTableHead align="center">Acciones</StyledTableHead>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No se han encontrado resultados
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedItems.map((row, idx) => (
                      <TableRow
                        key={row.id || idx}
                        className={idx % 2 === 0 ? "filaOscura" : ""}
                      >
                        <TableCell align="left" style={{ fontWeight: 600 }}>
                          {row.nombre}
                          {row.fast && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                ml: 1,
                                px: 1,
                                py: 0.2,
                                bgcolor: "#e8f5e9",
                                color: "#2e7d32",
                                borderRadius: 1,
                                fontWeight: "bold",
                              }}
                            >
                              Fast
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {row.esVenta == null
                            ? "-"
                            : row.esVenta
                              ? "Venta"
                              : "Arriendo"}
                        </TableCell>
                        <TableCell align="left">
                          {row.serviciosBase && row.serviciosBase.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {row.serviciosBase.map((s) => (
                                <li key={s.id || s.nombre}>{s.nombre}</li>
                              ))}
                            </ul>
                          ) : (
                            "Sin servicios"
                          )}
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: "100px" }}>
                          <Tooltip title="Editar" aria-label="editar">
                            <Link to={`/planes/editar/${row.id}`}>
                              <IconButton color="primary" size="small">
                                <EditIcon />
                              </IconButton>
                            </Link>
                          </Tooltip>

                          <Tooltip title="Eliminar" aria-label="eliminar">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={(e) => handleDeletePlan(e, row)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 20]}
              SelectProps={{
                native: true,
              }}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        )}
      </div>
    </div>
  );
};

export default ListadoPlanes;
